/**
 * An amount the airline pays, as `operator-bff:airline-money@v2` sends it (commercial-contract
 * §6.13): the PRICE, never Nexa's cost. Where the wire types it `LccPrice | null`, `null`
 * means the price is not available right now (no price card, `priced: false`) — show
 * «No disponible» and never buy at it.
 */
export interface LccPrice {
  amount: string;
  currency: string;
}
export interface LccPassenger {
  id: string;
  age: number;
}
/** An adult (18+) needs no age to search; the date of birth is checked at purchase. */
export interface LccAdultPassenger {
  id: string;
  type: 'adult';
}
/** Standalone searches send adults by type and children/infants by exact age — never both. */
export type LccPartyMember = LccAdultPassenger | LccPassenger;
export const isAdultMember = (p: LccPartyMember): p is LccAdultPassenger =>
  'type' in p && p.type === 'adult';
export interface LccProvider {
  id: 'duffel' | 'atlas';
  mode: string;
  configured: boolean;
  bookingEnabled: boolean;
}
export interface LccOffer {
  id: string;
  provider: 'duffel' | 'atlas';
  mode: 'sandbox' | 'production';
  /** The group total at the airline's price; `null` = not available (can't be bought). */
  price: LccPrice | null;
  expiresAt?: string;
  passengers: LccPartyMember[];
  warnings: string[];
  segments: Array<{
    flightNumber: string;
    marketingAirline: string;
    operatingAirline: string;
    origin: string;
    destination: string;
    departureLocal: string;
    arrivalLocal: string;
  }>;
}
export interface LccTraveler {
  id: string;
  givenName: string;
  familyName: string;
  bornOn: string;
  gender: 'm' | 'f';
  title: 'mr' | 'ms' | 'mrs' | 'miss' | 'dr';
  email: string;
  phoneNumber: string;
  nationality?: string;
  passport?: { number: string; expiresOn: string; issuingCountry: string };
  infantPassengerId?: string;
}
export interface LccPurchase {
  id: string;
  provider: 'duffel' | 'atlas';
  mode: 'sandbox' | 'production';
  status: 'SUBMITTED' | 'CONFIRMED' | 'PENDING' | 'UNKNOWN' | 'FAILED';
  orderId?: string;
  bookingReference?: string;
  ticketNumbers?: string[];
  /** The charged total at the airline's price; `null` = not available right now. */
  price?: LccPrice | null;
  code?: string;
  /** What the vendor refused and on which field (#768) — vendor vocabulary, never values. */
  vendorCode?: string;
  field?: string;
}
export interface LccSearch {
  caseUrn: string;
  groupId: string;
  origin: string;
  destination: string;
  departureDate: string;
  cabin: string;
  currency: string;
  passengers: LccPassenger[];
}
export interface LccPurchaseRequest {
  offerId: string;
  caseUrn: string;
  groupId: string;
  /** The PRICE the supervisor saw and approved. The BFF re-reads the cost and buys only if
   *  it still prices to this (409 `price_changed` otherwise). */
  approvedPrice: LccPrice;
  travelers: LccTraveler[];
  acceptanceConfirmed: true;
}
export type LccCandidateRequest = Omit<LccSearch, 'departureDate' | 'passengers'> & {
  departureDate?: string;
  passengers: Array<{ id: string; age?: number }>;
};
export interface LccCandidateResult {
  status: 'REQUIRES_PASSENGER_DETAILS' | 'READY' | 'NO_OPTIONS' | 'UNAVAILABLE';
  requirements: Array<{ field: 'departureDate' | 'age'; passengerId?: string }>;
  candidates: Array<{
    kind: 'LCC_PURCHASE_QUOTE';
    fulfillment: 'EXTERNAL_PURCHASE';
    hold: 'NONE';
    priceBasis: 'GROUP_TOTAL';
    approval: 'SUPERVISOR';
    autoSelect: false;
    offer: LccOffer;
  }>;
  errors: Array<{ provider: string; code: string }>;
  /** Searched as adults by the airline's ADT type, without a stated age — confirmed at purchase. */
  assumedAdults?: string[];
  /** `false` → the price card was unavailable and every offer price is `null`. */
  priced?: boolean;
}
